/* The counterparty card's Addresses tab (AD-2): the counterparty's retail points, then its own
   addresses — plus the "no retail points" notice (AD-4), which the card's head shows too.

   Points come from the card itself (`points` on `GET excalibur/counterparties/:id/`, active first,
   then by name), so the block costs no request. Unlinking is its own endpoint, not the form's PATCH:
     DELETE excalibur/counterparties/:id/points/:point_id/   → the full card
   and, as `LinksSection` does for linked counterparties, only `points` + `points_count` are handed
   up (`onPoints`), into the shown record and its baseline alike — an open form keeps its edits.
   ⚠ Success is "the answer has an `id`", never `isOk()`: a link already gone is a 404 `{detail}`,
   which `call()` hands over as data.

   Addresses are their own list (`excalibur/counterparty-addresses/?counterparty=`, primary first),
   fetched when the tab mounts and re-read after every write. A counterparty has a handful, so one
   page of 100 and no pager. DELETE is soft and answers 204 — a raw `Response` from `call()`.

   Writes (add / edit / delete an address, unlink a point) are offered to every role that can sign
   in — all of them may write the card (the API's rule). Every way out of the card — a point, a responsible merchandiser — goes through the card's `leaveTo`. */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert as Notice, Button, Empty, Flex, Modal, Skeleton, Table, Tag, Typography } from "antd";
import { AimOutlined, DeleteOutlined, DisconnectOutlined, EditOutlined, PlusOutlined, PushpinOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import { RowActions, actionsColumn, dash, textOf } from "../../component/table-cells";
import { COUNTERPARTIES_PAGE, TRANSLATION_DOWNLOADER } from "../../config/lang";
import { call, getAbortable } from "../../util/action";
import { translate } from "../../util/util";

import AddressModal from "./AddressModal";
import PointFromAddressModal from "./PointFromAddressModal";
import { LoadError, PersonLink, linkClick } from "./tabParts";

const ADDRESSES = "excalibur/counterparty-addresses/";
const toast = { position: "top-right" };

/** AD-4: a counterparty with no retail point cannot be worked in the field. `onGo` (only for a
    role that may write) leads to making one: the Addresses tab with the point modal open on the
    primary address without a point — or the new-address modal when there is no such address. */
export function NoPointsNotice({ lang, onGo, className }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  return (
    <Notice
      className={className}
      type="info"
      showIcon
      title={t("ad_nopoints_title")}
      description={t("ad_nopoints_body")}
      action={
        onGo ? (
          <Button size="small" icon={<PlusOutlined />} onClick={onGo}>
            {t("ad_create_point")}
          </Button>
        ) : null
      }
    />
  );
}

/* A muted one-line sub-line under a cell's main text, cut with "…" and a tooltip. */
const SubLine = ({ text }) =>
  text ? (
    <Typography.Text type="secondary" className="lp-main__sub" ellipsis={{ tooltip: text }}>
      {text}
    </Typography.Text>
  ) : null;

/* Two lines in one cell: the main text, and a muted line under it (the full lists' `TitleCell`).
   `sub2` is an optional second such line (the address's building type — its own line, so an
   ellipsis on the locality cannot swallow it and "·" does not make it read as part of the address).
   `note` is an optional last muted line shown in FULL — it wraps and keeps its line breaks, with no
   ellipsis and no tooltip (the address comment; spec/02's Table contract exception). */
const TwoLine = ({ main, sub, sub2, tags, note }) => (
  <Flex vertical className="lp-main">
    <Flex align="center" gap={6} wrap>
      {main}
      {tags}
    </Flex>
    <SubLine text={sub} />
    <SubLine text={sub2} />
    {note ? (
      <Typography.Text type="secondary" className="lp-main__sub cp-addr__note">
        {note}
      </Typography.Text>
    ) : null}
  </Flex>
);

const muted = text => (
  <Typography.Text type="secondary" ellipsis={{ tooltip: text }}>
    {text}
  </Typography.Text>
);

function PointsBlock({ counterpartyId, points, lang, leaveTo, onPoints }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);

  /* Re-read the card and keep only its points — after a point turned out to be unlinked already. */
  const refresh = () =>
    getAbortable(`excalibur/counterparties/${counterpartyId}/`)
      .then(response => response?.id && onPoints(response))
      .catch(() => {});

  /* Destructive, so confirmed — naming the point, saying what stays, the verb on the button. */
  const unlink = point =>
    Modal.confirm({
      title: t("ad_unlink_title").replace("{name}", point.name),
      content: t("ad_unlink_body"),
      okText: t("ad_unlink"),
      okButtonProps: { danger: true },
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: () =>
        new Promise(resolve =>
          call({ method: "DELETE", url: `excalibur/counterparties/${counterpartyId}/points/${point.id}/` }, response => {
            resolve();
            if (response?.id) {
              onPoints(response);
              Alert.success(t("ad_unlinked_msg"), toast);
              return;
            }
            if (response?.detail) Alert.warning(t("ad_unlink_gone_msg"), toast);
            refresh();
          })
        ),
    });

  const columns = [
    {
      key: "name",
      title: t("ad_col_point"),
      render: (v, row) => {
        const to = `/points/${row.id}`;
        return (
          <TwoLine
            main={
              <Link className="cp-doclink" to={to} title={row.name} onClick={linkClick(leaveTo, to)}>
                {textOf(row.name)}
              </Link>
            }
            tags={
              <>
                {row.is_active === false ? <Tag className="cp-flag">{t("ad_inactive")}</Tag> : null}
                {row.has_location === false ? (
                  <Tag className="cp-flag" icon={<AimOutlined />}>
                    {t("ad_no_location")}
                  </Tag>
                ) : null}
              </>
            }
            sub={[row.address, row.code].filter(Boolean).join(" · ")}
          />
        );
      },
    },
    {
      key: "locality",
      title: t("ad_col_locality"),
      render: (v, row) => (row.locality ? <TwoLine main={<Typography.Text>{row.locality.name}</Typography.Text>} sub={row.locality.county?.name} /> : dash),
    },
    {
      key: "network",
      title: t("ad_col_network"),
      render: (v, row) =>
        row.network?.name ? <Typography.Text ellipsis={{ tooltip: row.network.name }}>{row.network.name}</Typography.Text> : muted(t("ad_no_network")),
    },
    {
      key: "responsible",
      title: t("ad_col_responsible"),
      render: (v, row) =>
        row.responsible_merchandiser ? (
          <PersonLink user={row.responsible_merchandiser} to={`/merchandiser/${row.responsible_merchandiser.id}`} leaveTo={leaveTo} />
        ) : (
          muted(t("ad_no_responsible"))
        ),
    },
    actionsColumn((v, row) => (
      <RowActions
        label={`${t("row_actions")}: ${row.name}`}
        items={[
          { key: "open", label: t("ad_open_point"), icon: <PushpinOutlined /> },
          { key: "unlink", label: t("ad_unlink"), icon: <DisconnectOutlined />, danger: true },
        ]}
        onClick={key => (key === "open" ? leaveTo(null, `/points/${row.id}`) : unlink(row))}
      />
    )),
  ];

  return (
    <section className="cp-block" aria-label={t("ad_points_title")}>
      <Flex className="cp-block__head" align="center" gap="small" wrap>
        <Typography.Text strong className="cp-block__title">
          {t("ad_points_title")}
        </Typography.Text>
      </Flex>
      <Table
        className="lp-table__grid lp-fit"
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={points}
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("ad_points_empty")} /> }}
      />
    </section>
  );
}

function AddressesBlock({ counterpartyId, counterpartyName, lang, leaveTo, onPointAdded, createIntent, onIntentDone, onCountsChange }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  /* `{row}` while the modal is open: `{row: null}` for a new address. */
  const [modal, setModal] = useState(null);
  /* The address a point is being made from (AD-3). */
  const [pointFor, setPointFor] = useState(null);

  useEffect(() => {
    setError(false);
    /* Two quick writes mean two reloads; only the newer answer may land. */
    let stale = false;
    call({ method: "GET", url: `${ADDRESSES}?counterparty=${counterpartyId}&page_size=100` }, response => {
      if (stale) return;
      if (!response || response.failed || !Array.isArray(response.results)) {
        setRows([]);
        setError(true);
        return;
      }
      setRows(response.results);
    });
    return () => {
      stale = true;
    };
  }, [counterpartyId, reload]);

  const again = () => setReload(k => k + 1);

  /* The notice's "Create point" (`?create=point`), acted on once the list is in: the primary
     address without a point, else the first without one, else a new address. */
  useEffect(() => {
    /* Not on a failed load: an empty list then means "unknown", not "no free address". The
       parameter stays, so a successful Retry still acts on it. */
    if (!createIntent || rows === null || error) return;
    const free = rows.filter(r => !r.point);
    const target = free.find(r => r.is_primary) || free[0];
    if (target) setPointFor(target);
    else setModal({ row: null });
    onIntentDone();
  }, [createIntent, rows, error, onIntentDone]);

  const remove = row =>
    Modal.confirm({
      title: t("ad_delete_title").replace("{street}", row.street),
      content: t("ad_delete_body"),
      okText: t("ad_delete"),
      okButtonProps: { danger: true },
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: () =>
        new Promise(resolve =>
          call({ method: "DELETE", url: `${ADDRESSES}${row.id}/` }, response => {
            resolve();
            if (response?.status === 204) {
              Alert.success(t("ad_deleted_msg"), toast);
              onCountsChange?.();
            } else if (response?.detail) Alert.warning(t("edit_error_msg"), toast);
            again();
          })
        ),
    });

  /* Where the map opens for an address with no coordinates: the first sibling address that has them. */
  const located = (rows || []).find(r => r.location);
  const mapView = located ? { center: [located.location.latitude, located.location.longitude], zoom: 12 } : null;

  const columns = [
    {
      key: "address",
      title: t("ad_col_address"),
      render: (v, row) => (
        <TwoLine
          main={<Typography.Text strong>{textOf(row.street)}</Typography.Text>}
          tags={row.is_primary ? <Tag className="cp-flag cp-flag--primary">{t("ad_primary")}</Tag> : null}
          sub={[row.locality?.county?.name, row.locality?.name].filter(Boolean).join(" · ")}
          sub2={row.building_type?.trim()}
          note={row.comment?.trim()}
        />
      ),
    },
    {
      key: "type",
      title: t("ad_col_type"),
      render: (v, row) => (row.address_type?.label ? <Typography.Text>{row.address_type.label}</Typography.Text> : dash),
    },
    {
      key: "point",
      title: t("o_point"),
      render: (v, row) => {
        if (!row.point)
          /* AD-3: the row's one main action, in the place its point will take — `PlusOutlined`,
             spec/02's "add". */
          return (
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setPointFor(row)}>
              {t("ad_create_point")}
            </Button>
          );
        const to = `/points/${row.point.id}`;
        return (
          <Link className="cp-doclink cp-doclink--plain" to={to} title={row.point.name} onClick={linkClick(leaveTo, to)}>
            {textOf(row.point.name)}
          </Link>
        );
      },
    },
    actionsColumn((v, row) => (
      <RowActions
        label={`${t("row_actions")}: ${row.street}`}
        items={[
          { key: "edit", label: t("edit_btn"), icon: <EditOutlined /> },
          { key: "delete", label: t("ad_delete"), icon: <DeleteOutlined />, danger: true },
        ]}
        onClick={key => (key === "edit" ? setModal({ row }) : remove(row))}
      />
    )),
  ];

  const linked = (rows || []).filter(r => r.point).length;

  return (
    <section className="cp-block" aria-label={t("ad_title")}>
      <Flex className="cp-block__head" align="center" justify="space-between" gap="small" wrap>
        <Flex align="center" gap="small" wrap>
          <Typography.Text strong className="cp-block__title">
            {t("ad_title")}
          </Typography.Text>
          <Tag className="cp-flag">{t("picsell_data_tag")}</Tag>
          {rows && !error ? (
            <Typography.Text type="secondary" className="lp-num">
              {t("ad_summary")
                .replace("{n}", rows.length)
                .replace("{m}", linked)}
            </Typography.Text>
          ) : null}
        </Flex>
        <Button icon={<PlusOutlined />} onClick={() => setModal({ row: null })}>
          {t("ad_add")}
        </Button>
      </Flex>

      {rows === null ? (
        <Skeleton active title={false} paragraph={{ rows: 3 }} />
      ) : (
        <Table
          className="lp-table__grid lp-fit"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: "max-content" }}
          locale={{
            emptyText: error ? <LoadError lang={lang} onRetry={again} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("ad_empty")} />,
          }}
        />
      )}

      {modal ? (
        <AddressModal
          key={modal.row?.id || "new"}
          row={modal.row}
          counterpartyId={counterpartyId}
          lang={lang}
          mapView={mapView}
          onClose={() => setModal(null)}
          onSaved={() => {
            /* A new address changes the counterparty's count; an edit does not. */
            if (!modal.row) onCountsChange?.();
            setModal(null);
            again();
          }}
        />
      ) : null}

      {pointFor ? (
        <PointFromAddressModal
          key={pointFor.id}
          address={pointFor}
          counterpartyName={counterpartyName}
          lang={lang}
          onClose={() => setPointFor(null)}
          onGone={() => {
            setPointFor(null);
            again();
          }}
          onDone={({ point }) => {
            setPointFor(null);
            onPointAdded(point);
            again();
          }}
        />
      ) : null}
    </section>
  );
}

export default function AddressesTab({ item, lang, leaveTo, onPoints, onPointAdded, onCreatePoint, createIntent, onIntentDone, onCountsChange }) {
  return (
    <Flex vertical className="cp-addrtab">
      {item.points_count === 0 ? <NoPointsNotice lang={lang} onGo={onCreatePoint} /> : null}
      <PointsBlock counterpartyId={item.id} points={Array.isArray(item.points) ? item.points : []} lang={lang} leaveTo={leaveTo} onPoints={onPoints} />
      <AddressesBlock
        counterpartyId={item.id}
        counterpartyName={item.name}
        lang={lang}
        leaveTo={leaveTo}
        onPointAdded={onPointAdded}
        createIntent={createIntent}
        onIntentDone={onIntentDone}
        onCountsChange={onCountsChange}
      />
    </Flex>
  );
}
